import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertTriangle, AlertCircle, Info, CheckCheck, MessageCircle } from "lucide-react";

const LEVEL_STYLES = {
  critical: "border-l-destructive bg-destructive/5",
  warning: "border-l-brand-gold bg-brand-gold-soft/40",
  info: "border-l-brand-emerald bg-brand-emerald-soft/40",
};
const LEVEL_ICONS = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "hace unos segundos";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return `hace ${Math.floor(diff / 86400)} d`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/notifications/");
      setData(resp);
    } catch (_) {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh cada minuto
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch (_) { /* silencioso — el bell no debe romper la UI */ }
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); await load(); } catch (_) { /* silencioso */ }
  };

  const openAction = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) load(); }}
        className="relative h-10 w-10 flex items-center justify-center rounded-full bg-white border border-border hover:bg-brand-emerald-soft/40 transition"
        data-testid="notification-bell"
        aria-label="Notificaciones"
      >
        <Bell className="h-5 w-5 text-brand-ink" />
        {data.unread > 0 && (
          <span
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-brand-gold text-brand-ink text-[10px] font-bold flex items-center justify-center shadow-gold"
            data-testid="notification-badge"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[70vh] rounded-lg shadow-2xl border border-border bg-white overflow-hidden z-50"
          data-testid="notifications-dropdown"
        >
          <div className="p-3 border-b border-border bg-stone-50/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="FStudy" className="h-6 w-6" />
              <span className="font-serif-display font-semibold text-brand-ink">Notificaciones</span>
            </div>
            {data.unread > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={markAllRead}
                data-testid="mark-all-read-button"
                className="text-xs text-brand-emerald-dark hover:text-brand-emerald"
              >
                <CheckCheck className="h-3 w-3 mr-1" /> Marcar todo
              </Button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && !data.items.length ? (
              <div className="p-8 text-center text-sm text-stone-500">Cargando…</div>
            ) : data.items.length === 0 ? (
              <div className="p-10 text-center text-sm text-stone-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-stone-300" />
                Todo tranquilo. Sin notificaciones nuevas.
              </div>
            ) : (
              data.items.map((n) => {
                const isChat = n.kind === "chat_message";
                const Icon = isChat ? MessageCircle : (LEVEL_ICONS[n.level] || Info);
                const unread = Number(n.unread_count || 1);
                return (
                  <button
                    key={n.id}
                    onClick={() => openAction(n)}
                    className={`w-full text-left p-3 border-b border-border border-l-4 hover:bg-stone-50 transition ${LEVEL_STYLES[n.level] || ""} ${n.read ? "opacity-60" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${isChat ? "text-brand-emerald-dark" : n.level === "critical" ? "text-destructive" : n.level === "warning" ? "text-brand-gold-dark" : "text-brand-emerald-dark"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-brand-ink line-clamp-1 flex items-center gap-2">
                          <span className="truncate">{n.title}</span>
                          {isChat && unread > 1 && (
                            <Badge className="bg-brand-gold text-brand-ink border-brand-gold text-[10px] font-bold" data-testid={`notification-chat-count-${n.id}`}>
                              {unread}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-stone-600 line-clamp-2 mt-0.5">
                          {n.message}
                        </div>
                        <div className="text-[10px] text-stone-400 mt-1" title={formatDateTime(n.created_at)}>
                          {timeAgo(n.created_at)}{n.company_name && ` · ${n.company_name}`}
                        </div>
                      </div>
                      {!n.read && (
                        <span className="h-2 w-2 rounded-full bg-brand-gold shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
